export type RoutingDedupeSignalProtocol = 'BGP' | 'OSPF' | 'IS-IS' | 'BFD';
export type RoutingDedupeTopologyProtocol = 'bgp' | 'ospf' | 'isis' | 'bfd';

type SignalRoutingDedupeRow = {
  deviceId?: string | null;
  instance?: string | null;
  targetId?: string | null;
  attributes?: { labels?: unknown } | null;
};

type TopologyRoutingDedupeRow = {
  protocol: RoutingDedupeTopologyProtocol;
  deviceId: string;
  peer: string;
  area?: string | null;
  instance?: string | null;
  attributes?: { labels?: unknown } | null;
};

const stringRecordFromUnknown = (value: unknown): Record<string, string> => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string') {
      out[key] = raw;
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = String(raw);
    }
  }
  return out;
};

export const routingAdjacencyAreaFromLabels = (labels: Record<string, string>): string => {
  for (const area of [labels.area, labels.ospf_area, labels.ospfArea]) {
    const normalized = area?.trim() || '';
    if (normalized && normalized.toLowerCase() !== 'unknown') {
      return normalized;
    }
  }
  return '';
};

export const routingAdjacencyAreaDisplay = (area: string | null | undefined): string => {
  const normalized = area?.trim() || '';
  const dottedMatch = normalized.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!dottedMatch) {
    return normalized;
  }
  const octets = dottedMatch.slice(1).map((value) => Number(value));
  if (octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
    return normalized;
  }
  return String(((octets[0] * 256 + octets[1]) * 256 + octets[2]) * 256 + octets[3]);
};

export const routingAdjacencyAreaEquivalenceKey = (area: string | null | undefined): string => {
  const normalized = area?.trim() || '';
  return normalized === '' ? '0' : routingAdjacencyAreaDisplay(normalized);
};

const deviceIdFromTargetId = (targetId?: string | null): string => {
  const value = (targetId ?? '').trim();
  if (!value) {
    return '';
  }
  if (value.startsWith('ref:inventory.device:')) {
    return value.slice('ref:inventory.device:'.length);
  }
  if (value.startsWith('ref:inventory.interface:')) {
    const rest = value.slice('ref:inventory.interface:'.length);
    const hashIndex = rest.indexOf('#');
    return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
  }
  return '';
};

const signalRowDeviceId = (row: Pick<SignalRoutingDedupeRow, 'deviceId' | 'targetId'>): string => {
  return row.deviceId?.trim() || deviceIdFromTargetId(row.targetId);
};

const bgpPeerFromInstance = (instance?: string | null): string => {
  const trimmed = (instance ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (!trimmed.includes('=')) {
    return trimmed;
  }
  for (const part of trimmed.split('|')) {
    const [rawKey, rawValue] = part.split('=', 2);
    if ((rawKey?.trim() ?? '') === 'peer_addr') {
      return rawValue?.trim() ?? '';
    }
  }
  return '';
};

export const signalRoutingAdjacencyDedupeKey = (
  row: SignalRoutingDedupeRow,
  protocol: RoutingDedupeSignalProtocol
): string => {
  const deviceId = signalRowDeviceId(row);
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  const rawInstance = row.instance?.trim() || '';
  const instance = labels.instance || labels.vrf_name || labels.vrf || 'default';
  const peer =
    labels.peer ||
    labels.peer_addr ||
    labels.peer_address ||
    labels.bgp_peer ||
    labels.neighbor ||
    labels.neighbor_ip ||
    labels.remote_ip ||
    labels.destination ||
    (protocol === 'BGP' ? bgpPeerFromInstance(rawInstance) : rawInstance) ||
    instance ||
    row.targetId ||
    '';
  if (protocol === 'OSPF') {
    const area = routingAdjacencyAreaEquivalenceKey(routingAdjacencyAreaFromLabels(labels));
    return `${deviceId}\x00${protocol}\x00${area}\x00${peer}`;
  }
  if (protocol === 'BFD') {
    // A BFD peer can have independent sessions over multiple interfaces or
    // session types within one VRF.  The CLI collector provides a stable
    // signal_instance for that exact session; retain it so the dashboard
    // never coalesces an Up and Down session into one nondeterministic row.
    const discriminator =
      rawInstance ||
      [
        labels.interface_name || labels.local_interface || labels.if_name || labels.ifName || labels.ifDescr || labels.interface || '',
        labels.bfd_session_type || labels.session_type || labels.type || ''
      ].join('\x00');
    return `${deviceId}\x00${protocol}\x00${instance}\x00${peer}\x00${discriminator}`;
  }
  return `${deviceId}\x00${protocol}\x00${instance}\x00${peer}`;
};

export const topologyRoutingAdjacencyPeerKey = (
  row: Pick<TopologyRoutingDedupeRow, 'protocol' | 'deviceId' | 'peer' | 'area' | 'instance' | 'attributes'>
): string => {
  const protocol = row.protocol === 'isis' ? 'IS-IS' : row.protocol.toUpperCase();
  if (row.protocol === 'ospf') {
    return `${row.deviceId}\x00${protocol}\x00${routingAdjacencyAreaEquivalenceKey(row.area)}\x00${row.peer}`;
  }
  return `${row.deviceId}\x00${protocol}\x00${row.instance || 'default'}\x00${row.peer}`;
};

export const topologyRoutingAdjacencyPeerKeyCandidates = (
  row: Pick<TopologyRoutingDedupeRow, 'protocol' | 'deviceId' | 'peer' | 'area' | 'instance' | 'attributes'>
): string[] => {
  const primary = topologyRoutingAdjacencyPeerKey(row);
  if (row.protocol !== 'ospf') {
    return [primary];
  }
  const area = row.area?.trim() || '';
  if (routingAdjacencyAreaEquivalenceKey(area) !== '0') {
    return [primary];
  }
  const alternate = topologyRoutingAdjacencyPeerKey({
    ...row,
    area: area === '0' ? '' : '0'
  });
  if (alternate === primary) {
    return [primary];
  }
  return [
    primary,
    alternate
  ];
};

export const topologyRoutingAdjacencyMergeKey = (
  row: Pick<TopologyRoutingDedupeRow, 'protocol' | 'deviceId' | 'peer' | 'area' | 'instance' | 'attributes'>,
  hasExistingKey: (key: string) => boolean
): string => {
  const candidates = topologyRoutingAdjacencyPeerKeyCandidates(row);
  return candidates.find(hasExistingKey) ?? candidates[0] ?? '';
};

export const topologyRoutingAdjacencyRowKey = (row: TopologyRoutingDedupeRow): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return [
    topologyRoutingAdjacencyPeerKey(row),
    row.protocol,
    row.instance || '',
    labels.local_ip || '',
    labels.local_interface || '',
    labels.vrf || ''
  ].join('\x00');
};

export const topologyRoutingStateIsUp = (protocol: RoutingDedupeTopologyProtocol, state: string): boolean => {
  const normalized = state.trim().toLowerCase();
  if (protocol === 'bgp') {
    return normalized === 'established' || normalized === 'estab';
  }
  if (protocol === 'isis') {
    return normalized === 'up' || normalized === 'established' || normalized === 'full';
  }
  if (protocol === 'bfd') {
    return normalized === 'up' || normalized === 'established';
  }
  return normalized === 'full' || normalized === 'two-way' || normalized === '2-way';
};

export const shouldReplaceRoutingHealthSample = (
  previous: { up: boolean; timestamp: number } | undefined,
  next: { up: boolean; timestamp: number }
): boolean => {
  return !previous || next.timestamp > previous.timestamp || (next.timestamp === previous.timestamp && !next.up && previous.up);
};

export const topologyRoutingStateLabel = (protocol: RoutingDedupeTopologyProtocol, state: string): string => {
  const normalized = state.trim().toLowerCase();
  if (protocol === 'bgp' && normalized === 'estab') {
    return 'Established';
  }
  if (protocol === 'bfd' && (normalized === 'up' || normalized === 'down')) {
    return normalized;
  }
  return state;
};
